<template>
  <div class="page-wrap">

    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Permissions"
      subtitle="Manage access control and module permissions for society members"
      add-label="Add"
      :show-edit="true"
      :show-delete="true"
      @add="openAddDialog"
      @edit="openEditDialog"
      @delete="confirmDeleteDialog($event.id)"
      :columns="columns"
      :items="permissions"
      :loading="loading"
      :total-items="permissions.length"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Custom Cells -->
      <template #col-profile_url="{ item }">
        <div class="profile-cell">
          <div class="p-avatar-wrap">
            <img 
              v-if="item.profile_url" 
              :src="API_BASE_URL + '/uploads/' + item.profile_url" 
              class="p-avatar-img"
            />
            <div v-else class="p-avatar-placeholder" :style="{ background: primaryColor + '20', color: primaryColor }">
              {{ item.name ? item.name.charAt(0).toUpperCase() : 'U' }}
            </div>
          </div>
          <div class="p-info">
            <span class="p-name">{{ item.name }}</span>
            <span class="p-id" v-if="item.mobile_no">{{ item.country_code }} {{ item.mobile_no }}</span>
          </div>
        </div>
      </template>

      <template #col-house_number="{ item }">
        <div class="house-number-badge">
          <v-icon size="14" color="#94A3B8">mdi-home-outline</v-icon>
          <span v-if="item.block_name" class="flat-prefix">{{ item.block_name }}-</span>
          <span class="house-num">{{ item.house_number }}</span>
        </div>
      </template>

      <template #col-Permission="{ item }">
        <div class="modules-grid">
            {{ item.Permission }}
          <span v-if="!item.Permission || item.Permission.length === 0" class="dash">—</span>
        </div>
      </template>

    </PremiumTable>

    <!-- ── Add/Edit Dialog ──────────────────── -->
    <PremiumModal
      v-model="addPermissionDialog"
      :title="permission.id ? 'Edit Permission' : 'New Permission'"
      max-width="480"
      @close="closeDialog"
      :submit-text="permission.id ? 'Update Access' : 'Grant Access'"
      @submit="permission.id ? updatePermission(permission.id) : savePermission()"
    >
      <v-row dense>
        <!-- Block selector (multi-building only) -->
        <v-col v-if="useBlocks" cols="12">
          <PremiumSelect
            v-model="permission.flat_id"
            label="Block"
            :items="flats"
            item-text="name"
            item-value="id"
            placeholder="Select block"
            icon="mdi-office-building-outline"
            v-validate="!!permission.id ? '' : 'required'"
            name="flat"
            data-vv-as="Block"
            :error="errors.first('flat') || serverSideErrors.flat_id"
            @change="getFlatMember"
            :disabled="!!permission.id"
            :readonly="!!permission.id"
          />
        </v-col>

        <!-- Member selector -->
        <v-col cols="12">
          <PremiumSelect
            v-model="permission.member_id"
            label="Member"
            :items="members"
            item-text="display_name"
            item-value="id"
            placeholder="Select society member"
            icon="mdi-account-outline"
            v-validate="!!permission.id ? '' : 'required'"
            name="member"
            data-vv-as="Member"
            :error="errors.first('member') || serverSideErrors.member_id"
            :disabled="!!permission.id"
            :readonly="!!permission.id"
          />
          <span class="cm2-error" v-if="serverSideErrors.member_id">{{ serverSideErrors.member_id }}</span>
        </v-col>

        <!-- Modules / Access Level -->
        <v-col cols="12">
          <PremiumSelect
            v-model="permission.module_id"
            label="Modules / Access Level"
            :items="modules"
            item-text="name"
            item-value="id"
            placeholder="Select accessible modules"
            icon="mdi-shield-check-outline"
            :multiple="true"
            v-validate="'required'"
            name="module"
            data-vv-as="Module"
            :error="errors.first('module') || serverSideErrors.module_id"
          />
        </v-col>
      </v-row>
    </PremiumModal>

    <!-- ── Confirm Deletion ─────────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Revoke Permission"
      message="Are you sure you want to revoke all admin permissions for this member? They will lose access to the selected modules immediately."
      confirm-text="Yes, Revoke"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deletePermission"
    />

  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal  from "../../common/ConfirmModal.vue";
import PremiumModal  from "../../common/PremiumModal.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import filterMixin   from "../../../mixins/filterMixin";

export default {
  name: 'PermissionsList',
  mixins: [filterMixin],
  components: { PremiumTable, ConfirmModal, PremiumModal, PremiumSelect },

  data() {
    return {
      search:           "",
      permissions:      [],
      members:          [],
      flats:            [],
      modules:          [],
      loading:          false,
      deleteLoading:    false,
      addPermissionDialog: false,
      deleteDialog:     false,
      delete_id:        "",
      page:             1,
      itemsPerPage:     10,
      permission: {
        id:         null,
        member_id:  null,
        flat_id:    "",
        module_id:  [],
      },
      serverSideErrors: {},
      user: {},
    };
  },

  computed: {
    ...mapState('theme', ['isDark', 'primaryColor']),
    ...mapGetters('society', ['useBlocks', 'unitType']),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        { key: 'profile_url',   label: 'Member Name', width: '240px' },
        { key: 'house_number',  label: 'Unit', width: '120px' },
        { key: 'Permission',    label: 'Authorized Modules' },
      ];
    },
  },

  watch: {
    search() {
      this.page = 1;
      this.getPermissions();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
    },

    getPermissions() {
      this.loading = true;
      let url = `societyadmin/permissions?society_id=${this.user.id}`;
      if (this.search) url += `&search=${this.search}`;

      this.$axios.get(url).then(response => {
        this.loading = false;
        const data = response.data;
        if (data.status) {
          this.permissions = data.permissions;
        } else {
          this.showError(data.message);
          if (data.type === 'invalid') {
            localStorage.removeItem("user");
            this.$router.push("/societyadmin/login");
          }
        }
      }).catch(() => { this.loading = false; });
    },

    getMembers() {
      // When no blocks: fetch all society members (same as committee member)
      this.$axios.get(`societyadmin/members?society_id=${this.user.id}&pagination=false`).then(response => {
        if (response.data.status) {
          const arr = Array.isArray(response.data.members)
            ? response.data.members
            : response.data.members.data || [];
          this.members = arr.map(m => ({
            ...m,
            display_name: `${m.name} (${m.house_number})`,
          }));
        }
      });
    },

    getFlat() {
      return this.$axios.get("societyadmin/flats").then(response => {
        const data = response.data;
        if (data.status) {
          let flats = [...data.flats];
          if (this.unitType === 3 && this.useBlocks) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        }
      });
    },

    getFlatMember() {
      this.members = [];
      this.permission.member_id = null;
      let url = `/societyadmin/members?society_id=${this.user.id}`;
      if (this.permission.flat_id === "all_shops") {
        url += "&unit_type=2";
      } else if (
        this.permission.flat_id &&
        this.permission.flat_id !== "null" &&
        this.permission.flat_id !== ""
      ) {
        url = `/societyadmin/flats/${this.permission.flat_id}/members`;
      }
      this.$axios.get(url).then(response => {
        const data = response.data;
        if (data.status) {
          const arr = Array.isArray(data.members)
            ? data.members
            : data.members.data || [];
          this.members = arr.map(m => {
            const prefix = this.unitType == 3
              ? m.unit_type == 2 ? "Shop " : "House "
              : "";
            return { ...m, display_name: `${m.name} (${prefix}${m.house_number})` };
          });
        }
      });
    },

    getModules() {
      this.$axios.get("societyadmin/modules").then(response => {
        if (response.data.status) this.modules = response.data.modules;
      });
    },

    savePermission() {
      this.$validator.validateAll().then(success => {
        if (success) {
          this.permission.society_id = this.user.id;
          this.$axios.post("/societyadmin/permissions", this.permission).then(response => {
            const data = response.data;
            if (data.status) {
              this.getPermissions();
              this.closeDialog();
              this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: "Permissions granted successfully" });
            } else {
              this.handleErrors(data);
            }
          });
        }
      });
    },

    updatePermission(id) {
      this.$validator.validateAll().then(success => {
        if (success) {
          this.permission.society_id = this.user.id;
          this.$axios.put("/societyadmin/permissions/" + id, this.permission).then(response => {
            const data = response.data;
            if (data.status) {
              this.getPermissions();
              this.closeDialog();
              this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: "Permissions updated successfully" });
            } else {
              this.handleErrors(data);
            }
          });
        }
      });
    },

    deletePermission() {
      this.deleteLoading = true;
      this.$axios.delete("societyadmin/permissions/" + this.delete_id).then(response => {
        this.deleteLoading = false;
        const data = response.data;
        if (data.status) {
          this.deleteDialog = false;
          this.getPermissions();
          this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: data.message });
        } else {
          this.showError(data.message);
        }
      }).catch(() => { this.deleteLoading = false; });
    },

    getPermissionDetail(id) {
      this.loading = true;
      this.$axios.get("societyadmin/permissionsDetail?member_id=" + id).then(response => {
        this.loading = false;
        const data = response.data;
        if (data.status) {
          this.permission.member_id = id;
          this.permission.id        = id;
          this.permission.module_id = data.permissions.map(x => x.module_id);
        } else {
          this.showError(data.message);
        }
      }).catch(() => { this.loading = false; });
    },

    openAddDialog() {
      this.permission = { id: null, member_id: null, flat_id: "", module_id: [] };
      this.members = [];
      if (!this.useBlocks) this.getMembers();
      this.addPermissionDialog = true;
    },

    openEditDialog(item) {
      const flatId = item.flat_id ? (isNaN(item.flat_id) ? item.flat_id : Number(item.flat_id)) : null;
      this.permission = { 
        id: item.id, 
        member_id: item.id, 
        flat_id: flatId, 
        module_id: [] 
      };
      // Ensure flats loaded
      if (this.useBlocks && this.flats.length === 0) {
        this.getFlat();
      }
      // Load members for this block WITHOUT resetting member_id
      if (this.useBlocks && flatId) {
        let url = `/societyadmin/flats/${flatId}/members`;
        this.$axios.get(url).then(response => {
          const data = response.data;
          if (data.status) {
            const arr = Array.isArray(data.members) ? data.members : data.members.data || [];
            this.members = arr.map(m => ({
              ...m,
              display_name: `${m.name} (${m.house_number})`,
            }));
          }
        });
      } else {
        this.getMembers();
      }
      this.getPermissionDetail(item.id);
      this.addPermissionDialog = true;
    },

    confirmDeleteDialog(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    closeDialog() {
      this.addPermissionDialog = false;
      this.deleteDialog = false;
      this.permission = { id: null, member_id: null, flat_id: "", module_id: [] };
      this.members = [];
      this.$validator.reset();
      this.serverSideErrors = {};
    },

    handleErrors(data) {
      if (data.errors) {
        this.serverSideErrors = {};
        data.errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
      } else {
        this.showError(data.message);
      }
    },

    showError(msg) {
      this.setAlert({ showAlert: true, variant: "red accent-3", timeout: "2000", message: msg });
    }
  },

  mounted() {
    this.user   = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : {};
    
    // Initialize persistent filters
    this.initFilters('permission');

    this.getPermissions();
    if (this.useBlocks) {
      this.getFlat();
    } else {
      this.getMembers();
    }
    this.getModules();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Cells ── */
.profile-cell { display: flex; align-items: center; gap: 12px; }
.p-avatar-wrap { width: 42px; height: 42px; flex-shrink: 0; min-width: 42px; min-height: 42px; border-radius: 50%; overflow: hidden; background: var(--surface2); border: 1px solid var(--border); }
.p-avatar-img { width: 100%; height: 100%; object-fit: cover; }
.p-avatar-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 800; }

.p-info { display: flex; flex-direction: column; gap: 2px; }
.p-name { font-size: 13.5px; font-weight: 700; color: var(--tx); }
.p-id { font-size: 11px; font-weight: 500; color: var(--tx3); }

.unit-tag {
  display: inline-block; padding: 2px 8px; border-radius: 6px;
  background: var(--surface2, #F1F5F9); color: var(--tx2, #475569);
  font-size: 11.5px; font-weight: 700; border: 1px solid var(--border);
}
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  /* font-weight: 700; */
  color: var(--tx, #111827);
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}

.modules-grid { display: flex; flex-wrap: wrap; gap: 6px; padding: 4px 0; }
.module-pill {
  padding: 3px 10px; border-radius: 6px; border: 1px solid transparent;
  font-size: 11px; font-weight: 700; transition: all 0.2s;
}
.module-pill:hover { transform: translateY(-1px); box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

/* ── Add/Edit Dialog ── */
.cm2-field { display: flex; flex-direction: column; gap: 8px; }
.cm2-label { font-size: 12px; font-weight: 700; color: var(--tx3, #64748B); text-transform: uppercase; letter-spacing: 0.04em; }

.chip-premium { background: var(--brand, #0D9972) !important; color: #fff !important; font-weight: 700 !important; font-size: 11px !important; }
.cm2-error { font-size: 11px; color: #EF4444; font-weight: 600; margin-top: 4px; }
</style>
